import { useMemo, useState } from 'react';

import { LegalClientInnerItemEntity } from 'api/types/entity/legal/legalClientInnerItemEntity';
import { useFormContext } from 'libs/form/useFormContext';
import { useTranslation } from 'libs/i18n';
import { IconButton } from 'libs/ui';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { Icon } from 'libs/ui/Icon';
import {
  DialogDeleteAndReasonChange,
  DialogReasonChange,
  Section,
} from 'modules/client/common/components';

import { BranchesInfoCard } from './BranchesInfoCard';
import { BranchesItem } from './BranchesInfoCard/types';

export interface CompanyBranchesSectionProps {
  branches: LegalClientInnerItemEntity[];
  onClickAddBranch?: () => void;
  onClickBranch?: (id: string) => void;
  onDeleteBranch?: (id: string, reason: string) => Promise<void>;
  onArchiveBranch?: (id: string, reason: string) => Promise<void>;
  onDearchiveBranch?: (id: string, reason: string) => Promise<void>;
}

export const CompanyBranchesSection = ({
  branches,
  onClickAddBranch,
  onClickBranch,
  onDeleteBranch,
  onArchiveBranch,
  onDearchiveBranch,
}: CompanyBranchesSectionProps) => {
  const { t } = useTranslation();
  const { readOnly } = useFormContext();

  const [dialogLoading, setDialogLoading] = useState(false);
  const [activeItem, setActiveItem] = useState<BranchesItem>();

  const {
    isOpen: isOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
    open: openDialogDelete,
  } = useDialog();

  const {
    isOpen: isOpenArchiveDialog,
    onClose: onCloseArchiveDialog,
    open: openArchiveDialog,
  } = useDialog();

  const items: BranchesItem[] = useMemo(() => {
    const result = branches.map((h) => ({
      id: h.id,
      name: h.name,
      country: h.registrationCountry?.name || '',
      archived: h.archived,
      createdAt: h.createdAt,
    }));

    result.sort((a, b) =>
      a.archived === b.archived
        ? b.createdAt.getTime() - a.createdAt.getTime()
        : a.archived
        ? 1
        : -1
    );

    return result;
  }, [branches]);

  const onClickDelete = onDeleteBranch
    ? (row: BranchesItem) => {
        setActiveItem(row);
        openDialogDelete();
      }
    : undefined;

  const onClickArchive =
    onArchiveBranch || onDearchiveBranch
      ? (row: BranchesItem) => {
          setActiveItem(row);
          openArchiveDialog();
        }
      : undefined;

  const onDelete = async (reason: string) => {
    if (activeItem && onDeleteBranch) {
      setDialogLoading(true);
      try {
        await onDeleteBranch(activeItem.id, reason);
      } catch (error) {
        /* empty */
      }
      setDialogLoading(false);
    }
    onCloseDeleteDialog();
  };

  const onArchiveOrDearchive = async (reason: string) => {
    if (activeItem) {
      setDialogLoading(true);
      if (!activeItem.archived && onArchiveBranch) {
        try {
          await onArchiveBranch(activeItem.id, reason);
        } catch (error) {
          /* empty */
        }
      }

      if (activeItem.archived && onDearchiveBranch) {
        try {
          await onDearchiveBranch(activeItem.id, reason);
        } catch (error) {
          /* empty */
        }
      }

      onCloseArchiveDialog();
      setDialogLoading(false);
    }
  };

  return (
    <Section
      datax="companyBranchesSection"
      headerRight={
        !readOnly &&
        onClickAddBranch && (
          <IconButton
            icon={<Icon.Plus size={16} />}
            mode="outlined"
            sizeVariant="xs"
            onClick={onClickAddBranch}
          />
        )
      }
      title={t('client.legal.companyBranches.title')}
    >
      <BranchesInfoCard
        editable={!readOnly}
        items={items}
        onCellClick={
          onClickBranch ? (item) => onClickBranch(item.id) : () => {}
        }
        onClickArchive={onClickArchive}
        onClickDelete={onClickDelete}
      />

      {/** Delete dialog */}
      <DialogDeleteAndReasonChange
        deleteLoading={dialogLoading}
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        onDelete={onDelete}
      />

      {/** Archive dialog */}
      <DialogReasonChange
        isOpen={isOpenArchiveDialog}
        submitLoading={dialogLoading}
        onClose={onCloseArchiveDialog}
        onSave={onArchiveOrDearchive}
      />
    </Section>
  );
};
